import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { WELCOME_SEEN_KEY } from '../constants/welcome';

// The doorway. Before anything is asked of a visitor, they are welcomed — the way a compound
// welcomes someone who has travelled a long way. The lines type themselves out because an
// arrival should feel like it is happening now, not like a banner that was already there.

const GOLD = '#C9A558';
const GOLD_DEEP = '#8B6914';
// White copy sits over the gilded Africa heritage landscape. The image is already dark, so
// the scrim only deepens the right side, where the body copy sits over the gold map.

// The Nyansapo (wisdom knot) replaces the heart that sat beside the map in the original art.
const BACKGROUND = encodeURI('/images/gilded-african-heritage-nyansapo.webp');

/** Typed in order. Each line waits for the one above it to finish. */
const LINES = [
  { text: 'Akwaaba — Welcome Home.', kind: 'kicker' },
  { text: 'Welcome to Ghana. Welcome to Africa. Welcome to Your Heritage.', kind: 'heading' },
  { text: 'Discover your roots. Celebrate your identity. Carry the legacy forward.', kind: 'body' },
];

/** The motherland passage beside the welcome: static, read at the visitor's own pace. */
const MOTHERLAND = [
  'Across oceans, generations, and centuries, the spirit of Africa endures.',
  'She is the cradle of humanity, the keeper of ancient wisdom, and the heartbeat of a heritage that knows no borders.',
  'From the sacred traditions of Ghana to the farthest corners of the African diaspora, our stories, names, rhythms, and cultural treasures continue to unite us.',
];

const SPEED_MS = { kicker: 90, heading: 55, body: 22 };

/** Either way out of the doorway counts as having been welcomed. */
function markWelcomed() {
  try {
    sessionStorage.setItem(WELCOME_SEEN_KEY, '1');
  } catch {
    /* storage blocked — the landing gate falls through to the home page anyway */
  }
}

export default function HomeWelcomePage() {
  const navigate = useNavigate();
  const [lineIndex, setLineIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useSEO({
    title: 'Akwaaba — Welcome to Mama Africa',
    description: 'Welcome to Ghana. Discover your Akan day name, your heritage, and the stories behind it.',
    image: '/gilded-african-heritage-share.jpg',
  });

  // Someone who has asked for less motion gets the whole text at once rather than nothing.
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    if (reducedMotion) {
      setLineIndex(LINES.length);
      setDone(true);
      return undefined;
    }
    if (lineIndex >= LINES.length) {
      setDone(true);
      return undefined;
    }

    const line = LINES[lineIndex];
    let position = 0;

    timer.current = setInterval(() => {
      position += 1;
      setTyped(line.text.slice(0, position));

      if (position >= line.text.length) {
        clearInterval(timer.current);
        // A beat between lines, longer after the short ones so they land.
        const pause = line.kind === 'body' ? 700 : 420;
        timer.current = setTimeout(() => {
          setLineIndex((i) => i + 1);
          setTyped('');
        }, pause);
      }
    }, SPEED_MS[line.kind]);

    return () => {
      clearInterval(timer.current);
      clearTimeout(timer.current);
    };
  }, [lineIndex, reducedMotion]);

  /**
   * Every line is laid out in full from the first frame; typing only reveals it. The part not
   * yet typed is still there, just invisible, so line breaks and the column's height never
   * change — nothing on the page moves or resizes while the welcome is typing.
   */
  const renderLine = (line, index) => {
    const isPast = index < lineIndex;
    const isCurrent = index === lineIndex;
    const shown = isPast ? line.text : isCurrent ? typed : '';
    const showCaret = isCurrent && !done;
    const content = (
      <>
        <span>{shown}</span>
        {showCaret && <Caret />}
        <span aria-hidden="true" style={{ visibility: 'hidden' }}>{line.text.slice(shown.length)}</span>
      </>
    );
    const Tag = line.kind === 'heading' ? 'h1' : 'p';
    return (
      <Tag key={index} className={`ma-welcome-line ma-welcome-line--${line.kind}`} aria-label={line.text}>
        {content}
      </Tag>
    );
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#140C06' }}>
      {/* Pinned to the screen, not to the page: the picture is sized once to the viewport and
          never zooms or re-crops, however tall the text makes the page. */}
      <div aria-hidden className="ma-welcome-bg" style={{ backgroundImage: `url("${BACKGROUND}")` }} />

      {/* Light scrim: the landscape is already dark; it only deepens the gold map behind
          the right-hand copy so the white text holds. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(90deg, rgba(10,6,2,0.12) 0%, rgba(10,6,2,0.06) 48%, rgba(10,6,2,0.42) 100%), linear-gradient(0deg, rgba(10,6,2,0.22), transparent 45%)',
        }}
      />
      <main
        className="ma-welcome-layout"
        style={{
          position: 'relative', flex: '1 0 auto', minHeight: 'calc(100vh - 124px)', display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 440px)',
          alignItems: 'center', gap: 'clamp(28px,5vw,76px)',
          padding: 'clamp(28px,5vw,64px) clamp(20px,4vw,64px)',
          maxWidth: 1480, margin: '0 auto',
        }}
      >
        <section
          className="ma-welcome-brand"
          aria-label="Mama Africa Afia and Ghanaian heritage"
          style={{ animation: reducedMotion ? 'none' : 'maWelcomeArrive 0.9s ease-out both' }}
        >
          <div className="ma-welcome-brand-copy">
            <p className="ma-welcome-brand-kicker"><span /> MAMA AFRICA · THE MOTHERLAND</p>
            <h2>Where Our Roots Run Deep, Our Ancestors Speak, and Our Legacy Lives On.</h2>
            <div className="ma-welcome-brand-intro">
              {MOTHERLAND.map((line) => <p key={line}>{line}</p>)}
              <p className="ma-welcome-brand-motto">Our roots are our identity. Our heritage is our pride. Our legacy is forever.</p>
            </div>
            {/* Phones stack the welcome text above the buttons, which pushes them well below
                the fold — so on small screens the certificate button also sits up here. */}
            <button
              type="button"
              className="ma-welcome-enter ma-welcome-mobile-cta"
              onClick={() => {
                markWelcomed();
                navigate('/certificate');
              }}
            >
              <span>Discover your Akan day name</span>
            </button>
          </div>
        </section>

        <div className="ma-welcome-panel">
          <div className="ma-welcome-copy">
            {LINES.map(renderLine)}
          </div>

        {/* The way onward is there from the first second: the welcome keeps typing beside it,
            but nobody has to wait for it to finish. The certificate leads — it is the reason
            most people came. */}
        <div
          className="ma-welcome-copy"
          style={{
            marginTop: 'clamp(28px,4vw,44px)',
            animation: reducedMotion ? 'none' : 'maWelcomeArrive 0.8s ease-out 0.4s both',
          }}
        >
          <button
            type="button"
            onClick={() => {
              markWelcomed();
              navigate('/certificate');
            }}
            className="ma-welcome-enter"
          >
            <span>Discover your Akan day name</span>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="12" x2="19" y2="12" />
              <polyline points="13 6 19 12 13 18" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => {
              markWelcomed();
              navigate('/');
            }}
            className="ma-welcome-store"
          >
            Explore our heritage
          </button>

          <a
            href="https://www.youtube.com/@MamaAfricaAfia"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Mama Africa Afia on YouTube"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9,
              margin: '18px 0 0 18px', color: '#FFFFFF',
              fontFamily: "'Montserrat', sans-serif", fontSize: 12,
              letterSpacing: '0.08em', textDecoration: 'none',
              textShadow: '0 1px 10px rgba(0,0,0,0.85)',
            }}
          >
            <svg width="20" height="15" viewBox="0 0 20 15" fill="none" aria-hidden="true">
              <rect width="20" height="15" rx="4" fill="#FF0000" />
              <path d="M8 4.2L13 7.5L8 10.8V4.2Z" fill="#FFFFFF" />
            </svg>
            Watch Mama Africa Afia on YouTube
          </a>

        </div>
        </div>
      </main>

      <footer className="ma-welcome-footer">
        <div className="ma-welcome-footer-inner">
          <div className="ma-welcome-footer-brand">
            <Link to="/" onClick={markWelcomed} aria-label="Mama Africa home">MAMA AFRICA</Link>
            <p>Ghanaian culture, stories, and the spirit of home, shared across generations.</p>
          </div>

          <div className="ma-welcome-footer-group">
            <p>Explore</p>
            <Link to="/store" onClick={markWelcomed}>Heritage keepsakes</Link>
            <Link to="/culture" onClick={markWelcomed}>Culture & heritage</Link>
            <Link to="/about" onClick={markWelcomed}>Our story</Link>
          </div>

          <div className="ma-welcome-footer-group">
            <p>Connect</p>
            <a className="ma-welcome-footer-channel" href="https://www.youtube.com/@MamaAfricaAfia" target="_blank" rel="noopener noreferrer" aria-label="Visit the Mama Africa Afia YouTube channel">
              <svg width="20" height="15" viewBox="0 0 20 15" fill="none" aria-hidden="true">
                <rect width="20" height="15" rx="4" fill="#FF0000" />
                <path d="M8 4.2L13 7.5L8 10.8V4.2Z" fill="#FFFFFF" />
              </svg>
              <span>Watch on YouTube</span>
            </a>
            <span className="ma-welcome-footer-handle">@MamaAfricaAfia</span>
            <a href="mailto:Mamaafricaafia@gmail.com">Contact Mama Africa</a>
          </div>

          <div className="ma-welcome-footer-group">
            <p>Information</p>
            <Link to="/shipping-returns" onClick={markWelcomed}>Shipping & returns</Link>
            <Link to="/privacy-policy" onClick={markWelcomed}>Privacy policy</Link>
            <Link to="/terms" onClick={markWelcomed}>Terms & conditions</Link>
            <Link to="/cookie-policy" onClick={markWelcomed}>Cookie policy</Link>
          </div>
        </div>
        <div className="ma-welcome-footer-bottom">
          <span>© 2026 Mama Africa Official. All rights reserved.</span>
          <span>Gye Nyame · Except God, I fear none</span>
        </div>
      </footer>

      <style>{`
        .ma-welcome-bg {
          position: fixed; inset: 0;
          background-size: cover; background-position: center; background-repeat: no-repeat;
        }
        /* Portrait phones crop the wide picture: keep the map, the heart of it, in view. */
        @media (max-aspect-ratio: 1/1) { .ma-welcome-bg { background-position: 74% center; } }

        .ma-welcome-line { color: #FFFFFF; margin: 0; }
        .ma-welcome-line--kicker {
          font: 400 clamp(12px,1.4vw,15px)/1.5 'Cinzel', serif; letter-spacing: 0.3em; text-transform: uppercase;
          text-shadow: 0 1px 10px rgba(0,0,0,0.85); margin: 0 0 16px;
        }
        .ma-welcome-line--heading {
          font: 700 clamp(28px,3.6vw,46px)/1.16 'Playfair Display', serif; margin: 0 0 20px;
          text-shadow: 0 2px 18px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9);
        }
        .ma-welcome-line--body {
          font: 400 clamp(17px,1.9vw,21px)/1.7 'EB Garamond', serif;
          text-shadow: 0 1px 12px rgba(0,0,0,0.85);
        }
        @keyframes maWelcomeArrive {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes maCaretBlink { 0%, 45% { opacity: 1; } 50%, 100% { opacity: 0; } }

        .ma-welcome-brand {
          display: block;
          min-width: 0;
        }
        .ma-welcome-brand-copy {
          min-width: 0;
          max-width: 610px;
          padding: 22px 0;
          color: #FFFFFF;
          text-shadow: 0 2px 18px rgba(0,0,0,0.85);
        }
        .ma-welcome-brand-kicker {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 16px;
          font: 600 10px/1.4 'Montserrat', sans-serif;
          letter-spacing: 0.18em;
          color: #FFFFFF;
        }
        .ma-welcome-brand-kicker span {
          width: 24px;
          height: 1px;
          flex: 0 0 auto;
          background: #F3C969;
        }
        .ma-welcome-brand-copy h2 {
          max-width: 570px;
          margin: 0;
          color: #FFFFFF;
          font: 700 clamp(30px,3.6vw,50px)/1.12 'Playfair Display', serif;
          text-shadow: 0 2px 18px rgba(0,0,0,0.88);
        }
        .ma-welcome-brand-intro {
          max-width: 520px;
          margin: 20px 0 30px;
          color: #FFFFFF;
          font: 19px/1.6 'EB Garamond', serif;
          text-shadow: 0 1px 12px rgba(0,0,0,0.9);
        }
        .ma-welcome-brand-intro p { margin: 0 0 10px; }
        .ma-welcome-brand-motto {
          margin-top: 16px !important; color: #F8D57D;
          font-family: 'Playfair Display', serif; font-style: italic; font-size: 1.04em;
        }
        .ma-welcome-mobile-cta { display: none !important; }
        @media (max-width: 680px) {
          .ma-welcome-mobile-cta { display: inline-flex !important; margin: 0 0 24px; }
        }
        .ma-welcome-panel {
          width: 100%;
          max-width: 440px;
        }
        .ma-welcome-copy { width: 100%; }
        @media (max-width: 900px) {
          .ma-welcome-layout {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 22px;
            min-height: auto;
            padding: 44px 32px !important;
          }
          .ma-welcome-brand-kicker { font-size: 9px; letter-spacing: 0.12em; }
          .ma-welcome-brand-copy { max-width: 700px; }
          .ma-welcome-brand-copy h2 { font-size: clamp(32px,5vw,44px); }
          .ma-welcome-brand-intro { font-size: 18px; margin: 14px 0 24px; }
          .ma-welcome-panel { max-width: 700px; }
        }
        @media (max-width: 680px) {
          .ma-welcome-layout {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 24px;
            padding: 28px 20px 36px !important;
          }
          .ma-welcome-brand-copy { padding: 10px 0 0; }
          .ma-welcome-brand-kicker { margin-bottom: 8px; font-size: 8px; }
          .ma-welcome-brand-copy h2 { font-size: clamp(28px,7.6vw,38px); }
          .ma-welcome-brand-intro { font-size: 17px; line-height: 1.45; margin: 12px 0 22px; }
          .ma-welcome-copy h1 { font-size: clamp(26px,7vw,36px) !important; }
          .ma-welcome-panel { max-width: 100%; }
        }

        .ma-welcome-footer {
          position: relative;
          z-index: 2;
          border-top: 1px solid rgba(248,213,125,0.48);
          background: linear-gradient(145deg, rgba(10,13,15,0.96), rgba(5,7,9,0.97));
          color: #FFFFFF;
          backdrop-filter: blur(12px);
        }
        .ma-welcome-footer-inner {
          display: grid;
          grid-template-columns: minmax(240px,1.5fr) repeat(3,minmax(130px,1fr));
          gap: 34px;
          width: min(1280px,calc(100% - 48px));
          margin: 0 auto;
          padding: 30px 0 26px;
        }
        .ma-welcome-footer-brand > a {
          color: #FFFFFF;
          font: 600 17px/1.3 'Cinzel', serif;
          letter-spacing: 0.16em;
          text-decoration: none;
        }
        .ma-welcome-footer-brand > p {
          max-width: 300px;
          margin: 9px 0 0;
          color: rgba(255,255,255,0.78);
          font: 15px/1.6 'EB Garamond', serif;
        }
        .ma-welcome-footer-group {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 9px;
        }
        .ma-welcome-footer-group > p {
          margin: 0 0 2px;
          color: #F8D57D;
          font: 600 10px/1.3 'Montserrat', sans-serif;
          letter-spacing: 0.15em;
          text-transform: uppercase;
        }
        .ma-welcome-footer-group > a {
          color: rgba(255,255,255,0.78);
          font: 12px/1.45 'Montserrat', sans-serif;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .ma-welcome-footer-group > a:hover { color: #FFFFFF; text-decoration: underline; }
        .ma-welcome-footer-group > .ma-welcome-footer-channel {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 9px 11px;
          border: 1px solid rgba(255,255,255,0.24);
          border-radius: 4px;
          color: #FFFFFF;
          text-decoration: none;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .ma-welcome-footer-group > .ma-welcome-footer-channel:hover {
          background: rgba(255,0,0,0.16);
          border-color: rgba(255,0,0,0.75);
          text-decoration: none;
        }
        .ma-welcome-footer-handle {
          color: rgba(255,255,255,0.58);
          font: 10px/1.4 'Montserrat', sans-serif;
          letter-spacing: 0.06em;
          padding-left: 2px;
        }
        .ma-welcome-footer-bottom {
          display: flex;
          justify-content: space-between;
          gap: 12px 24px;
          width: min(1280px,calc(100% - 48px));
          margin: 0 auto;
          padding: 14px 0 18px;
          border-top: 1px solid rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.62);
          font: 10px/1.5 'Montserrat', sans-serif;
          letter-spacing: 0.02em;
        }
        @media (max-width: 900px) {
          .ma-welcome-footer-inner { grid-template-columns: repeat(2,minmax(0,1fr)); gap: 26px 32px; }
        }
        @media (max-width: 600px) {
          .ma-welcome-footer-inner { width: calc(100% - 40px); gap: 24px 18px; }
          .ma-welcome-footer-brand { grid-column: 1 / -1; }
          .ma-welcome-footer-bottom { width: calc(100% - 40px); flex-direction: column; }
        }

        .ma-welcome-enter {
          display: inline-flex; align-items: center; gap: 16px;
          padding: 15px 30px; border-radius: 999px; cursor: pointer;
          background: linear-gradient(135deg, ${GOLD} 0%, #B98F3E 55%, ${GOLD_DEEP} 100%);
          border: none;
          color: #FFFDF7;
          box-shadow: 0 10px 26px rgba(139,105,20,0.28);
          font-family: 'Cinzel', serif; font-size: 14px; letter-spacing: 0.2em;
          text-transform: uppercase;
          backdrop-filter: blur(6px);
          transition: background 0.35s ease, border-color 0.35s ease, transform 0.35s ease;
        }
        .ma-welcome-enter:hover {
          transform: translateX(6px);
          box-shadow: 0 14px 32px rgba(139,105,20,0.38);
        }
        @media (max-width: 520px) {
          .ma-welcome-enter { gap: 10px; padding: 14px 20px; font-size: 12px; letter-spacing: 0.1em; max-width: 100%; }
        }
        .ma-welcome-enter svg { transition: transform 0.35s ease; flex: 0 0 auto; }
        .ma-welcome-enter:hover svg { transform: translateX(5px); }

        /* Quieter than the primary way onward: an invitation, not a demand. */
        .ma-welcome-store {
          margin-left: 18px;
          padding: 15px 4px;
          background: none; border: none; cursor: pointer;
          color: #FFFFFF;
          text-shadow: 0 1px 10px rgba(0,0,0,0.85);
          font-family: 'Montserrat', sans-serif; font-size: 12.5px;
          letter-spacing: 0.14em; text-transform: uppercase;
          border-bottom: 1px solid rgba(233,200,124,0.55);
          transition: color 0.3s ease, border-color 0.3s ease;
        }
        .ma-welcome-store:hover { color: #FBF0D6; border-color: #F6E7C2; }
        @media (max-width: 620px) {
          .ma-welcome-store { display: block; margin: 18px 0 0; }
        }
      `}</style>
    </div>
  );
}

function Caret() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block', width: '0.055em', minWidth: 2, height: '1em',
        background: '#FFFFFF', marginLeft: '0.12em', verticalAlign: '-0.12em',
        animation: 'maCaretBlink 1s step-end infinite',
      }}
    />
  );
}
